import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ICandidateModel} from '../../../core/interfaces/candidate.interface';
import {Injectable} from '@angular/core';
import {AttachmentService} from '../../../core/attachment.service';
import {SectionBase} from './base.section';
import {EditAssessmentRequestModel} from '../../edit-assessment-request.model';
import {UploadErrorDialogComponent} from '../../../shared/upload-error-dialog/upload-error-dialog.component';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import { PsValidators } from 'app/core/validators/email.validator';
import { IPronounSelectItemModel } from 'app/core/interfaces/select-item.interface';

@Injectable()
export class CandidateSection extends SectionBase {
  private _modal: NgbModalRef;
  private _pronouns: IPronounSelectItemModel[];
  
  constructor(private fb: UntypedFormBuilder,
              private attachmentService: AttachmentService,
              private modalService: NgbModal,) {
    super();
  }

  public get typeControl() {
    return this.formGroup.controls['type'] as UntypedFormControl;
  }

  public get positionControl() {
    return this.formGroup.controls['position'] as UntypedFormControl;
  }

  public get confidentialControl() {
    return this.formGroup.controls['confidential'] as UntypedFormControl;
  }

  public get firstNameControl() {
    return this.formGroup.controls['firstName'] as UntypedFormControl;
  }

  public get lastNameControl() {
    return this.formGroup.controls['lastName'] as UntypedFormControl;
  }

  public get emailControl() {
    return this.formGroup.controls['email'] as UntypedFormControl;
  }

  public get pronounControl() {
    return this.formGroup.controls['pronoun'] as UntypedFormControl;
  }

  public get noResumeControl() {
    return this.formGroup.controls['noResume'] as UntypedFormControl;
  }

  public get resumeAttachmentIdControl() {
    return this.formGroup.controls['resumeAttachmentId'] as UntypedFormControl;
  }

  public get originalFilenameControl() {
    return this.formGroup.controls['originalFilename'] as UntypedFormControl;
  }

  public get acknowledgmentControl() {
    return this.formGroup.controls['acknowledgment'] as UntypedFormControl;
  }
  
  public init({assessmentRequest, pronouns}: { assessmentRequest: EditAssessmentRequestModel,  pronouns: IPronounSelectItemModel[] }) {
    super.setAssessmentRequest(assessmentRequest);
    this.formGroup = this.buildFormGroup();

    const candidate = this.assessmentRequest.candidate;

    if (!pronouns) {
      throw new Error('pronouns must be specified');
    }

    this._pronouns = pronouns;

    const pronoun = candidate.pronounCode
      ? this._pronouns.find((c) => c.pronounCode === candidate.pronounCode)
      : null;

    if (pronoun) {
      this.pronounControl.setValue(pronoun);
    }

    this.typeControl.setValue(candidate.candidateType);
    this.positionControl.setValue(candidate.position);
    this.confidentialControl.setValue(this.assessmentRequest.isConfidential);
    this.firstNameControl.setValue(candidate.firstName);
    this.lastNameControl.setValue(candidate.lastName);
    this.emailControl.setValue(candidate.email);

    this.noResumeControl.setValue(this.assessmentRequest.candidateNoResume);
    this.resumeAttachmentIdControl.setValue(this.assessmentRequest.resumeAttachmentId);
    this.originalFilenameControl.setValue(this.assessmentRequest.originalFilename);

    // if (this.assessmentRequest.hasParent) {
    //   this.formGroup.disable();
    // }
    this.syncPositionRequired(this.typeControl.value as 'I' | 'E' | null);

this.typeControl.valueChanges.subscribe((t: 'I' | 'E' | null) => {
  this.syncPositionRequired(t);
});
  }

  public clear() {
    this.typeControl.setValue(null);
    this.positionControl.setValue(null);
    this.confidentialControl.setValue(false);
    this.firstNameControl.setValue(null);
    this.lastNameControl.setValue(null);
    this.emailControl.setValue(null);
    this.pronounControl.setValue(null);
    this.noResumeControl.setValue(false);
    this.resumeAttachmentIdControl.setValue(null);
    this.originalFilenameControl.setValue(null);
  }

  public get formValue(): any {
    const values = this.formGroup.value;

    const candidateModel = {
      candidateType: values.type,
      position: values.position,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      pronounCode: values.pronoun?.pronounCode ?? null,
    } as ICandidateModel;

    return {
      candidate: candidateModel,
      candidateNoResume: values.noResume,
      resumeAttachmentId: values.resumeAttachmentId,
      isConfidential: values.confidential
    };
  }

  public onUploadResumeFileChange(event) {
    const fileList: FileList = event.target.files;
    const allowedExtensions = ['pdf', 'doc', 'docx'];

    if (fileList.length) {
      const file: File = fileList[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
      this._modal = this.modalService.open(UploadErrorDialogComponent);
      const errorDialogInstance = this._modal.componentInstance as UploadErrorDialogComponent;

      errorDialogInstance.title = 'Invalid File Type';
      errorDialogInstance.body = `
        The selected file type is not allowed.
        <br/><br/>
        Please upload a file with one of the following extensions:
        <strong>.pdf, .doc, or .docx</strong>.
      `;
      errorDialogInstance.onConfirm.subscribe(() => {
        (event.target as HTMLInputElement).value = '';
      });
      return;
    }
      this.attachmentService.upload(file).subscribe(
        (attachmentId) => {
          this.resumeAttachmentIdControl.setValue(attachmentId);
          this.resumeAttachmentIdControl.markAsDirty();
          this.originalFilenameControl.setValue(file.name);
      },
       error => {
          this._modal = this.modalService.open(UploadErrorDialogComponent);
          const errorDialogInstance = this._modal.componentInstance as UploadErrorDialogComponent;
          errorDialogInstance.title = 'Error Uploading File';
          errorDialogInstance.body = `An error occurred while trying to attach the resume.
          <br/>
          <br/>Please try again or contact us at:
          <br/><a href='tel:314-993-8008'>314-993-8008</a> or <a href='mailto:testing@leadershipall.com'>testing@leadershipall.com</a>.
          <br/>`;
          errorDialogInstance.onConfirm.subscribe(
            (isConfirmed) => {
              if (isConfirmed) {
                event.target.value = '';
              }
          })
       });
    }
  }

  protected buildFormGroup(): UntypedFormGroup {
    return this.fb.group({
      type: [null, Validators.required],
      position: [null, Validators.maxLength(200)],
      confidential: null,
      firstName: [null, [Validators.required, Validators.maxLength(200)]],
      lastName: [null, [Validators.required, Validators.maxLength(200)]],
      email: [null, Validators.compose([PsValidators.email(), Validators.required, Validators.maxLength(254)])],
      pronoun: [null, Validators.required],
      noResume: null,
      resumeAttachmentId: null,
      originalFilename: null,
      acknowledgment: [false, Validators.requiredTrue]
    });
  }

  private syncPositionRequired(candidateType: 'I' | 'E' | null): void {
  const ctrl = this.positionControl;

  const baseValidators = [Validators.maxLength(200)];

  if (candidateType === 'I') {
    ctrl.setValidators([...baseValidators, Validators.required]);
  } else {
    ctrl.setValidators(baseValidators);

    if (ctrl.errors?.required) {
      const { required, ...rest } = ctrl.errors;
      ctrl.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  ctrl.updateValueAndValidity({ emitEvent: false });
}

}
